import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { superAdminApi } from '@/services/superAdminApi';
import { Building2, Plug, DollarSign, CheckCircle2, XCircle, ArrowUpRight } from 'lucide-react';

const Stat = ({ icon: Icon, label, value, sub, accent, to }) => {
  const body = (
    <div
      data-testid={`sa-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:border-slate-300 hover:bg-slate-100"
    >
      <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
      <div className="mt-1 text-sm font-medium text-slate-700">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
      {to && <ArrowUpRight className="absolute right-5 top-5 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiHealth, setAiHealth] = useState(null);

  useEffect(() => {
    superAdminApi.overview()
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    superAdminApi.platformAiHealth()
      .then((r) => setAiHealth(r.data))
      .catch(() => setAiHealth(null));
  }, []);

  if (loading) return <div className="text-slate-600">Lädt…</div>;
  if (!data) return <div className="text-rose-600">Übersicht konnte nicht geladen werden.</div>;

  return (
    <div data-testid="sa-dashboard" className="space-y-8">
      {aiHealth && aiHealth.known && !aiHealth.ok && (
        <div data-testid="sa-ai-health-alert" className="flex items-start gap-3 rounded-2xl border border-rose-300 bg-rose-50 p-4">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="text-sm">
            <p className="font-semibold text-rose-800">KI-Ausfall erkannt</p>
            <p className="mt-0.5 text-rose-700">
              {aiHealth.reason || 'Der Plattform-KI-Standard ist aktuell nicht erreichbar.'}
              {aiHealth.checked_at ? ` · zuletzt geprüft: ${new Date(aiHealth.checked_at).toLocaleString('de-CH')}` : ''}
            </p>
            <Link to="/superadmin/integrations" className="mt-1 inline-block font-medium text-rose-800 underline">
              KI-Keys prüfen & testen →
            </Link>
          </div>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Plattform-Übersicht</h1>
        <p className="mt-1 text-sm text-slate-600">Zentrale Steuerung für StrongFit und angebundene Praxen.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat icon={Building2} label="Mandanten aktiv" value={`${data.tenants.active}`} sub={`${data.tenants.total} gesamt`} accent="bg-indigo-500/15 text-indigo-700" to="/superadmin/tenants" />
        <Stat icon={Plug} label="Integrationen" value={`${data.integrations.configured}/${data.integrations.total}`} sub="eingerichtet" accent="bg-emerald-500/15 text-emerald-700" to="/superadmin/integrations" />
        <Stat icon={DollarSign} label="KI-Kosten (30T)" value={`$${data.ai_usage_30d.cost_usd}`} sub={`${data.ai_usage_30d.calls} Aufrufe`} accent="bg-amber-500/15 text-amber-700" to="/superadmin/usage" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-medium text-slate-900">Integrationen (global)</h2>
        <p className="mb-5 text-sm text-slate-600">Diese Schlüssel gelten plattformweit für alle Mandanten.</p>
        <div className="divide-y divide-slate-200">
          {data.integrations.items.map((it) => (
            <div key={it.key} data-testid={`sa-integration-${it.key}`} className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-slate-800">{it.name}</div>
                <div className="text-xs text-slate-500">{it.detail}{it.masked ? ` · ${it.masked}` : ''}</div>
              </div>
              {it.configured ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Aktiv
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                  <XCircle className="h-3.5 w-3.5" /> Offen
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
